import { ComboboxItem } from '@ariakit/react';
import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { useMemo } from 'react';

import { GlobalSearchResult, GlobalSearchResultProps } from '../global-search';
import { OverflowContainer } from '../overflow-tooltip';
import { markdownAsPlainText } from '../table/columns';
import { Card } from './base-card';
import { TagConfig } from './card-tags';

export interface SearchResultCardProps extends GlobalSearchResult {
  name: string | undefined | null;
  description: string | undefined | null;
  tags: TagConfig[];
}

export const SearchResultCard = ({ result }: GlobalSearchResultProps<SearchResultCardProps>) => {
  const { entityTag, statusTag, defaultTags } = useMemo(() => {
    const entityTag = result.tags.find((tag) => tag.type === 'entity');
    const statusTag = result.tags.find((tag) => tag.type === 'status');
    const defaultTags = result.tags.filter((tag) => tag.type === 'default');

    return { entityTag, statusTag, defaultTags };
  }, [result.tags]);

  const plainTextDescription = markdownAsPlainText(result.description);

  return (
    <Box
      as={ComboboxItem}
      value={result.id}
      w="full"
      focusOnHover
      blurOnHoverEnd={false}
      resetValueOnSelect={false}
      rounded="lg"
      _selected={{ backgroundColor: useColorModeValue('gray.100', 'gray.600') }}
      _hover={{ backgroundColor: useColorModeValue('gray.100', 'gray.600') }}
      __css={{
        '&[data-active-item]': {
          backgroundColor: useColorModeValue('gray.100', 'gray.600'),
        },
      }}
    >
      <Card size="md" position="relative" aria-label="search result" gap={2}>
        <Card.Header left={<Card.Tags tags={[entityTag, statusTag]} />} />

        <Card.Body flexDirection="column" alignItems="normal" gap={3}>
          <OverflowContainer as={Box} align="normal">
            <Flex direction="column" gap={1}>
              <OverflowContainer.Tooltip
                label={result.name}
                placement="top"
                gutter={12}
                openDelay={500}
              >
                <Text size="sm" weight="medium" isTruncated>
                  {result.name}
                </Text>
              </OverflowContainer.Tooltip>
              <OverflowContainer.Tooltip
                label={plainTextDescription}
                placement="top"
                gutter={12}
                openDelay={500}
              >
                <Text size="xs" color="muted" isTruncated>
                  {plainTextDescription}
                </Text>
              </OverflowContainer.Tooltip>
            </Flex>
          </OverflowContainer>
          <Card.Tags flexWrap="wrap" tags={defaultTags} />
        </Card.Body>
      </Card>
    </Box>
  );
};
